import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X, Cookie, Shield, BarChart3, Megaphone } from "lucide-react";

const CONSENT_KEY = "cab_cookie_consent";
const CONSENT_VERSION = 1;

// Reads consent from localStorage. Returns null when user has not chosen yet.
export function readConsent() {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.v !== CONSENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeConsent(consent) {
  try {
    window.localStorage.setItem(
      CONSENT_KEY,
      JSON.stringify({ v: CONSENT_VERSION, ts: Date.now(), ...consent })
    );
  } catch {
    /* storage disabled: ignore */
  }
}

// Applies consent to any third-party trackers already on the page (PostHog).
function applyConsent(consent) {
  if (typeof window === "undefined") return;
  const analyticsOk = !!consent?.analytics;
  const ph = window.posthog;
  if (ph && typeof ph.opt_in_capturing === "function") {
    try {
      if (analyticsOk) ph.opt_in_capturing();
      else ph.opt_out_capturing();
    } catch {
      /* ignore */
    }
  }
  window.__cabConsent = consent;
  window.dispatchEvent(new CustomEvent("cab:consent-updated", { detail: consent }));
}

export default function CookieBanner() {
  const [open, setOpen] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [prefs, setPrefs] = useState({ necessary: true, analytics: false, marketing: false });

  useEffect(() => {
    const existing = readConsent();
    if (!existing) {
      // No choice yet -> opt-out until user chooses (GDPR default)
      applyConsent({ necessary: true, analytics: false, marketing: false });
      // Small delay so the banner doesn't fight the hero animation
      const t = setTimeout(() => setOpen(true), 900);
      return () => clearTimeout(t);
    }
    applyConsent(existing);
  }, []);

  const acceptAll = () => {
    const c = { necessary: true, analytics: true, marketing: true };
    writeConsent(c);
    applyConsent(c);
    setOpen(false);
  };

  const rejectAll = () => {
    const c = { necessary: true, analytics: false, marketing: false };
    writeConsent(c);
    applyConsent(c);
    setOpen(false);
  };

  const savePrefs = () => {
    const c = { necessary: true, analytics: prefs.analytics, marketing: prefs.marketing };
    writeConsent(c);
    applyConsent(c);
    setShowPrefs(false);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <div
      data-testid="cookie-banner"
      className="fixed inset-x-0 bottom-0 z-[100] px-3 pb-3 sm:px-6 sm:pb-6"
      role="dialog"
      aria-live="polite"
      aria-label="Preferenze cookie"
    >
      <div className="mx-auto max-w-[1100px] overflow-hidden rounded-2xl border border-cab-deep/10 bg-cream shadow-[0_20px_60px_-20px_rgba(20,30,60,0.35)] backdrop-blur">
        {!showPrefs ? (
          <div className="flex flex-col gap-5 p-5 sm:p-7 md:flex-row md:items-center md:gap-8">
            <div className="flex items-start gap-3 md:flex-1">
              <span className="mt-1 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cab/10 text-cab">
                <Cookie className="h-5 w-5" />
              </span>
              <div>
                <p className="font-display text-base font-bold uppercase tracking-wide text-cab-deep">Rispettiamo la tua privacy</p>
                <p className="mt-2 text-sm leading-relaxed text-ink/80">
                  Usiamo cookie tecnici necessari al funzionamento del sito e, con il tuo consenso, cookie di analisi anonima per capire come viene usato. Puoi accettare tutti, rifiutare i non essenziali o personalizzare le preferenze.{" "}
                  <Link to="/cookie-policy" data-testid="cookie-banner-policy-link" className="font-semibold text-cab underline underline-offset-2 hover:text-cab-deep">
                    Cookie Policy
                  </Link>
                  .
                </p>
              </div>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap md:justify-end">
              <button
                type="button"
                onClick={() => setShowPrefs(true)}
                data-testid="cookie-banner-preferences"
                className="rounded-md border border-cab-deep/20 px-4 py-2.5 text-sm font-semibold text-cab-deep transition-colors hover:bg-cab-deep/5"
              >
                Personalizza
              </button>
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-banner-reject"
                className="rounded-md border border-cab-deep/20 px-4 py-2.5 text-sm font-semibold text-cab-deep transition-colors hover:bg-cab-deep/5"
              >
                Rifiuta non essenziali
              </button>
              <button
                type="button"
                onClick={acceptAll}
                data-testid="cookie-banner-accept"
                className="rounded-md bg-cab px-5 py-2.5 text-sm font-bold text-cream transition-colors hover:bg-cab-deep"
              >
                Accetta tutti
              </button>
            </div>
          </div>
        ) : (
          <div className="p-5 sm:p-7">
            <div className="mb-5 flex items-start justify-between gap-4">
              <div>
                <p className="font-display text-base font-bold uppercase tracking-wide text-cab-deep">Preferenze cookie</p>
                <p className="mt-1 text-sm text-ink/70">Scegli quali categorie autorizzare. Puoi cambiare idea in ogni momento.</p>
              </div>
              <button
                type="button"
                onClick={() => setShowPrefs(false)}
                aria-label="Chiudi preferenze"
                data-testid="cookie-prefs-close"
                className="rounded-md p-2 text-ink/60 hover:bg-cab-deep/5 hover:text-cab-deep"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="space-y-3">
              <PrefRow
                icon={<Shield className="h-4 w-4" />}
                title="Necessari"
                desc="Indispensabili per il funzionamento del sito (routing, preferenze cookie). Sempre attivi."
                checked
                disabled
              />
              <PrefRow
                icon={<BarChart3 className="h-4 w-4" />}
                title="Analitici"
                desc="Statistiche anonime per capire come viene usato il sito (PostHog aggregato)."
                checked={prefs.analytics}
                onChange={(v) => setPrefs((p) => ({ ...p, analytics: v }))}
                testId="cookie-pref-analytics"
              />
              <PrefRow
                icon={<Megaphone className="h-4 w-4" />}
                title="Marketing"
                desc="Attualmente non usati. Riservato a future campagne/social pixel."
                checked={prefs.marketing}
                onChange={(v) => setPrefs((p) => ({ ...p, marketing: v }))}
                testId="cookie-pref-marketing"
              />
            </div>

            <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={rejectAll}
                data-testid="cookie-prefs-reject"
                className="rounded-md border border-cab-deep/20 px-4 py-2.5 text-sm font-semibold text-cab-deep transition-colors hover:bg-cab-deep/5"
              >
                Rifiuta non essenziali
              </button>
              <button
                type="button"
                onClick={savePrefs}
                data-testid="cookie-prefs-save"
                className="rounded-md bg-cab px-5 py-2.5 text-sm font-bold text-cream transition-colors hover:bg-cab-deep"
              >
                Salva preferenze
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function PrefRow({ icon, title, desc, checked, onChange, disabled, testId }) {
  return (
    <label className={`flex items-start justify-between gap-4 rounded-lg border border-cab-deep/10 bg-white/50 p-4 ${disabled ? "opacity-70" : "hover:border-cab/30"}`}>
      <span className="flex items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 items-center justify-center rounded-md bg-cab/10 text-cab">{icon}</span>
        <span>
          <span className="block font-display text-sm font-bold uppercase tracking-wide text-cab-deep">{title}</span>
          <span className="mt-0.5 block text-xs text-ink/70">{desc}</span>
        </span>
      </span>
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange && onChange(e.target.checked)}
        data-testid={testId}
        className="mt-1 h-5 w-9 shrink-0 cursor-pointer appearance-none rounded-full bg-cab-deep/20 transition-all before:absolute before:mt-0.5 before:ml-0.5 before:h-4 before:w-4 before:rounded-full before:bg-white before:transition-transform checked:bg-cab checked:before:translate-x-4 disabled:cursor-not-allowed"
        style={{ position: "relative" }}
      />
    </label>
  );
}
